import React, { useEffect, useState } from 'react';
import { View, Image, TouchableOpacity, FlatList } from 'react-native';
import Header from '../../../components/Header';
import DropdownButton from '../../../components/DropdownButton';
import { faChevronDown, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { translate } from '../../../services/translate';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import Card from '../../../components/Card';
import MyAppText from '../../../components/MyAppText';
import Divider from '../../../components/Divider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWifiExclamation } from '@fortawesome/pro-regular-svg-icons';
import moment, { Moment } from 'moment';
import Badge, { BadgeColor } from '../../../components/Badge';
import { DEFAULT_PASTEL_COLORS, DEFAULT_PASTEL_TEXT_COLORS } from '../../../styles/colorPalettes';
import { ScrollView } from 'react-native-web-hover';
import FormInput from '../../../components/FormInput';
import { useForm, useWatch } from 'react-hook-form';
import CustomButton from '../../../components/CustomButton';
import TicketReasonDropdown from './TicketReasonDropdown';
import { useQuery } from '@tanstack/react-query';
import { crmService } from '../../../services/central-api/crm';
import { useAuth } from '../../../context/Auth';
import Toast from 'react-native-toast-message';
import { removeDeletedDateFromSerialNo } from '../../../utils/removeDeletedDateFromSerialNo';
import EmailPreviewModal from './EmailPreviewModal';
import DeleteModal from '../../../components/DeleteModal';
import Container from '../../../components/Container';
import { useCurrentApp } from '../../../context/CurrentApp';
import { openInNewTab } from '../../../services/navigation-assistant';
import { RootNavigation } from '../../../Routes';

function getReasonTagTextColor(color: string) {
    const index = DEFAULT_PASTEL_COLORS.indexOf(color);
    return DEFAULT_PASTEL_TEXT_COLORS[index];
}

function Report(props: { type: 'report'; value: TicketReport; } | { type: 'status'; value: StatusHistory; } | { type: 'assign'; value: { createdAt: string; responsibleUser: TicketResponsibleUser; }; }) {
    const { styles } = useStyles(stylesheet);

    if (props.type === 'report') {
        return <View style={{ gap: 8 }}>
            <View style={styles.reportHeader}>
                {props.value.user ?
                    <Image style={styles.userPin} source={require(`../../../../assets/SentinelX_pin.png`)} />
                    : <View style={styles.userPin}>
                        <FontAwesomeIcon icon={faWifiExclamation as IconDefinition} />
                    </View>}
                <View style={{ flexGrow: 1, justifyContent: 'center', gap: 4 }}>
                    {props.value.user && <MyAppText style={styles.reportUser}>{props.value.user.name}</MyAppText>}
                    <Badge mode='ROUNDED' size={'small'} backgroundColor={props.value.reason.color} textColor={getReasonTagTextColor(props.value.reason.color)}>{props.value.reason.name}</Badge>
                </View>
                <MyAppText style={styles.reportDate}>{moment(props.value.createdAt).format('DD/MM/YYYY HH:mm:ss')}</MyAppText>
            </View>
            <MyAppText style={styles.reportDetail}>{props.value.observation}</MyAppText>
        </View>;
    } else if (props.type === 'status') {
        return <View style={{ gap: 8 }}>
            <View style={styles.reportHeader}>
                <View style={{ flexDirection: 'row' }}>
                    <MyAppText style={styles.statusText}>{translate('ticketStatusChangedTo')}{' '}</MyAppText>
                    <MyAppText style={[styles.statusText, { fontWeight: 'bold' }]}>{props.value.status.title}.</MyAppText>
                </View>
                <MyAppText style={styles.reportDate}>{moment(props.value.createdAt).format('DD/MM/YYYY HH:mm:ss')}</MyAppText>
            </View>
        </View>;
    } else {
        return <View style={{ gap: 8 }}>
            <View style={styles.reportHeader}>
                <View style={{ flexDirection: 'row' }}>
                    <MyAppText style={styles.statusText}>{translate('ticketAssignedBy')}{' '}</MyAppText>
                    <MyAppText style={[styles.statusText, { fontWeight: 'bold' }]}>{props.value.responsibleUser.name}.</MyAppText>
                </View>
                <MyAppText style={styles.reportDate}>{moment(props.value.createdAt).format('DD/MM/YYYY HH:mm:ss')}</MyAppText>
            </View>
        </View>;
    }


}

export interface TicketReportForm {
    reasonId: number | null;
    observation: string;
}

function SideBarItem(props: { title: string; content: string; direction: 'row' | 'column'; contentTextColor?: string; }) {
    const { styles } = useStyles(stylesheet);

    return <View style={{ flexDirection: props.direction, gap: props.direction === 'row' ? 8 : 0 }}>
        <MyAppText style={styles.sideBarItemTitle}>{props.title}:</MyAppText>
        <MyAppText style={[styles.sideBarItemContent, props.contentTextColor ? { color: props.contentTextColor } : undefined]}>{props.content}</MyAppText>
    </View>;
}

export default function TicketDetailComponent({ navigation, route, page }: {
    navigation: RootNavigation;
    route: {
        params: {
            id: number;
        };
    };
    page: 'ticket-detail' | 'ticket-camera-detail' | 'kanban-ticket-detail';
}) {
    const { styles, theme } = useStyles(stylesheet);
    const { isParentApp } = useCurrentApp();
    const { user: authenticatedUser } = useAuth();
    const [duration, setDuration] = useState('');
    const [selectedReason, setSelectedReason] = useState<TicketReason>();
    const [isEmailPreviewModalVisible, setIsEmailPreviewModalVisible] = useState(false);
    const [isConfirmEmailWithoutAckModalVisible, setIsConfirmEmailWithoutAckModalVisible] = useState(false);

    const defaultEmptyValues: TicketReportForm = {
        reasonId: null,
        observation: '',
    };

    const form = useForm<TicketReportForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    const selectedReasonId = useWatch({
        control: form.control,
        name: 'reasonId',
    });

    function getDuration(paramStart: string, paramFinish: string | null) {
        const finish = paramFinish ? moment(paramFinish) : moment();
        const start = moment(paramStart);

        const totalSeconds = Math.max(0, finish.diff(start, 'seconds'));

        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        const pad = (n: number) => String(n).padStart(2, '0');

        return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    }

    const queryResult = useQuery({
        queryKey: ['ticketDetail', route.params.id],
        queryFn: () => crmService.getTicket(route.params.id),
        enabled: !!route.params.id && !!authenticatedUser,
    });

    const { data, refetch, isLoading } = queryResult;


    useEffect(() => {
        if (selectedReasonId) {
            getTicketReasonById(selectedReasonId);
        }
    }, [selectedReasonId]);

    function parseMinutesToTime(minutes: number, format: 'HHhMMmin' | 'HH:MM:SS') {
        const totalSeconds = minutes * 60;

        const hours = Math.floor(totalSeconds / 3600);
        const internalMinutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        const pad = (n: number) => String(n).padStart(2, '0');

        if (format === 'HHhMMmin') {
            return `${pad(hours)}h${pad(internalMinutes)}min`;
        }

        return `${pad(hours)}:${pad(internalMinutes)}:${pad(seconds)}`;
    }

    async function getTicketReasonById(ticketReasonId: number) {
        try {
            const ticketReason = await crmService.getTicketReason(ticketReasonId);
            setSelectedReason(ticketReason);
            setDuration(parseMinutesToTime(ticketReason.duration, 'HH:MM:SS'));
            form.setValue('observation', ticketReason.observation);
            await form.trigger();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    async function assignTicket(ticketId: number) {
        try {
            await crmService.ackOfflineEvent(ticketId);
            refetch();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    async function sendReport() {
        try {
            const values = form.getValues();

            if (!form.formState.isValid || !values.reasonId) {
                return;
            }

            await crmService.sendTicketReport(route.params.id, {
                observation: values.observation,
                reasonId: values.reasonId,
            });

            setIsEmailPreviewModalVisible(false);
            refetch();
            form.reset(defaultEmptyValues);
            setDuration('');
            setSelectedReason(undefined);
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    /**
     *
     * @param intervals
     * @returns time in minutes
     */
    function calculateSlaTotal(intervals: { start: Moment; finish: Moment; }[]) {

        const sorted = intervals
            .slice()
            .sort((a, b) => a.start.valueOf() - b.start.valueOf());

        const merged = [];

        for (const current of sorted) {
            const last = merged[merged.length - 1];

            if (!last || current.start.isAfter(last.finish)) {
                merged.push({
                    start: current.start.clone(),
                    finish: current.finish.clone(),
                });
            } else {
                last.finish = moment.max(last.finish, current.finish);
            }
        }

        return merged.reduce((total, i) => total + i.finish.diff(i.start, 'minutes'), 0);
    }

    function calculateSla(ticket: CameraHistory) {

        let badgeColor: BadgeColor;

        if (!ticket.finish) {
            const time = calculateSlaTotal([
                // default time for ticket to be validated
                // FIXME: remove hardcoded 50 min
                { start: moment(ticket.start), finish: moment(ticket.start).add(50, 'minutes') },
                ...ticket.reports.map(report => ({
                    start: moment(report.createdAt),
                    finish: moment(report.createdAt).add(moment.duration(report.reason.duration, 'minutes')),
                }))]);

            const ticketDuration = moment().diff(moment(ticket.start), 'minutes');

            const percent = ticketDuration / time;


            if (percent < .25) {
                badgeColor = 'green';
            } else if (percent < .5) {
                badgeColor = 'blue';
            } else if (percent < .75) {
                badgeColor = 'orange';
            } else {
                badgeColor = 'red';
            }

        } else {

            let time = 0;

            if (ticket.reports.length) {
                time = calculateSlaTotal([
                    // FIXME: default time for ticket to be validated
                    { start: moment(ticket.start), finish: moment(ticket.start).add(50, 'minutes') },
                    ...ticket.reports.map(report => ({
                        start: moment.min(moment(ticket.finish), moment(report.createdAt)),
                        finish: moment.min(moment(ticket.finish), moment(report.createdAt).add(moment.duration(report.reason.duration, 'minutes'))),
                    }))]);
            }

            const ticketDuration = moment(ticket.finish).diff(moment(ticket.start), 'minutes');

            if (time >= ticketDuration) {
                badgeColor = 'green';
            } else {
                badgeColor = 'red';
            }
        }

        return <Badge mode='SQUARE' color={badgeColor}>{translate('totalSLA')}: {parseMinutesToTime(calculateSlaTotal(ticket.reports.map(report => ({
            start: moment(report.createdAt),
            finish: moment(report.createdAt).add(moment.duration(report.reason.duration, 'minutes')),
        }))), 'HHhMMmin')}</Badge>;

    }

    function renderItem({ item }: {
        item: {
            type: 'report'; value: TicketReport;
        } | {
            type: 'status'; value: StatusHistory;
        } | {
            type: 'assign'; value: { createdAt: string; responsibleUser: TicketResponsibleUser; };
        };
    }) {
        return <Report {...item} />;
    }

    if (isLoading || !data) {
        return <></>;
    }

    function getStatus(finish: string | null, status?: TicketStatus) {
        if (finish) {
            return <Badge mode='SQUARE' color='green'>{translate('concluded')}</Badge>;
        }
        if (status) {
            return <Badge mode='SQUARE' color={status.color as BadgeColor}>{status.title}</Badge>;
        }
        return <Badge mode='SQUARE' color='orange'>{translate('open')}</Badge>;
    }

    return <Container>
        <Header
            goBackAction={() => {
                if (navigation.canGoBack()) {
                    return navigation.goBack();
                }
                if (page === 'kanban-ticket-detail') {
                    return navigation.navigate('CRM', {
                        screen: 'OfflineEventsKanban', params: {
                            companyId: data.camera.companyId,
                        }
                    });
                }
                if (page === 'ticket-camera-detail') {
                    return navigation.navigate('CRM', {
                        screen: 'CameraHistory', params: {
                            page: 1,
                            limit: 25,
                            orderBy: 'start',
                            orderDirection: 'asc',
                            onlyOffline: false,
                            id: data.cameraId,
                        }
                    });
                }
                if (page === 'ticket-detail') {
                    return navigation.navigate('CRM', {
                        screen: 'OfflineEvents', params: {
                            page: 1,
                            limit: 25,
                            orderBy: 'start',
                            orderDirection: 'asc',
                            onlyOffline: false,
                        }
                    });
                }
            }}
            title={`${translate('disconnectionEvent')}: #${data.id}`}
            actions={<DropdownButton
                viewStyle={{ height: '100%' }}
                isButton
                actions={[{
                    content: translate('openCameraConfigurationOnNewTab'),
                    onPress: () => {
                        openInNewTab('CRM', { screen: 'ManagementDetails', params: { id: data.camera.id } });
                    },
                }, {
                    content: translate('watchCameraOnNewTab'),
                    onPress: () => {
                        openInNewTab('Cameras', { screen: 'CameraMapPlayer', params: { id: data.camera.id } });
                    },
                }]}
                buttonIcon={faChevronDown}
                buttonType={'secondary'}
                buttonLabel='actions'
                offsetX={-118}
                offsetY={45}
                size={320}
            />}
        />
        <View style={styles.container}>
            <Card style={{ gap: 16 }}>
                <View style={styles.cardTitleContainer}>
                    <MyAppText style={styles.cardTitle}>{translate('historic')}</MyAppText>
                </View>
                <View style={styles.reports}>
                    <FlatList
                        style={{
                            padding: 16,
                        }}
                        data={[
                            ...data.reports.map(r => ({ type: 'report' as const, value: r })),
                            ...data.statusHistory.map(s => ({ type: 'status' as const, value: s })),
                            ...(() => {
                                const defaultStatus: { type: 'status'; value: StatusHistory; }[] = [{
                                    type: 'status',
                                    value: {
                                        createdAt: data.createdAt,
                                        status: {
                                            id: -1,
                                            title: translate('open'),
                                            color: '#F0D1AC',
                                        },
                                    },
                                }];

                                if (data.finish) {
                                    defaultStatus.push({
                                        type: 'status',
                                        value: {
                                            createdAt: data.finish,
                                            status: {
                                                id: -1,
                                                title: translate('concluded'),
                                                color: '#CCE1CF',
                                            },
                                        },
                                    });
                                }

                                return defaultStatus;
                            })(),
                            ...(() => {
                                const assign: { type: 'assign'; value: { createdAt: string; responsibleUser: TicketResponsibleUser; }; }[] = [];

                                if (data.assignedAt && data.responsibleUser) {
                                    assign.push({
                                        type: 'assign',
                                        value: {
                                            createdAt: data.assignedAt,
                                            responsibleUser: data.responsibleUser,
                                        }
                                    });
                                }

                                return assign;
                            })(),
                        ].sort((a, b) => {
                            if (a.value.createdAt === b.value.createdAt) {
                                if (a.type === 'status') {
                                    return -1;
                                }
                                return 1;
                            }
                            return moment(a.value.createdAt).isBefore(moment(b.value.createdAt)) ? -1 : 1;
                        })}
                        renderItem={renderItem}
                        ItemSeparatorComponent={() => <View style={{ paddingVertical: 16 }}><Divider color={theme.colors.backgrounds['BG-1']} /></View>}
                    />
                </View>
                {isParentApp && !data.finish && <>
                    <Divider />
                    <View style={{ gap: 8 }}>
                        <View style={{ flexDirection: 'row', gap: 8 }}>
                            <View style={{ flex: 1 }}>
                                <TicketReasonDropdown
                                    form={form}
                                    zIndex={1}
                                    fieldName='reasonId'
                                    required={true}
                                    ticketId={route.params.id}
                                />
                            </View>
                            <View style={{ width: 110 }}>
                                <FormInput
                                    label={translate('duration')}
                                    value={duration}
                                    onChangeText={() => { }}
                                    placeholder='HH:MM:SS'
                                    disabled={true}
                                />
                            </View>
                        </View>
                        <View style={{ flexDirection: 'row', gap: 8 }}>
                            <View style={{ flex: 1 }}>
                                <FormInput
                                    form={form}
                                    fieldName='observation'
                                    placeholder={translate('enterAComment')}
                                    multiline
                                    inputStyle={{ height: 110 }}
                                    required
                                    disabled={form.watch('reasonId') === null}
                                    maxLength={65535}
                                />
                            </View>
                            <View style={{ width: 110, justifyContent: 'flex-end' }}>
                                <CustomButton
                                    onPress={() => {
                                        if (data.responsibleUserId === authenticatedUser?.id) {
                                            setIsEmailPreviewModalVisible(true);
                                        } else {
                                            setIsConfirmEmailWithoutAckModalVisible(true);
                                        }
                                    }}
                                    text={translate('send')}
                                    disabled={!form.formState.isValid}
                                />
                            </View>
                        </View>
                    </View>
                </>}
            </Card>
            <Divider color={theme.colors.neutral['300']} orientation='vertical' />
            <Card style={{ maxWidth: 382, padding: 24, gap: 16 }}>
                <View style={styles.cardTitleContainer}>
                    <MyAppText style={styles.cardTitle}>{translate('general_info')}</MyAppText>
                    {getStatus(data.finish, data.statusHistory.at(-1)?.status)}
                </View>
                <ScrollView contentContainerStyle={{ gap: 16 }}>
                    <View style={{ gap: 8 }}>
                        <MyAppText style={styles.sideBarTitle}>{translate('responsible')}</MyAppText>
                        {data.responsibleUser ? <View style={{ flexDirection: 'row', gap: 8 }}>
                            <MyAppText style={styles.sideBarItemContent}>{data.responsibleUser.warName || data.responsibleUser.name}</MyAppText>
                        </View> : <View style={{ flexDirection: 'row', gap: 8 }}>
                            <MyAppText style={styles.sideBarItemContent}>{translate('noOne')}</MyAppText>
                            {isParentApp && !data.finish && <TouchableOpacity onPress={() => assignTicket(route.params.id)}>
                                <MyAppText style={styles.ackText}>{translate('assumeTicket')}</MyAppText>
                            </TouchableOpacity>
                            }
                        </View>}
                    </View>
                    <Divider color={theme.colors.backgrounds['BG-1']} />
                    <View style={{ gap: 16 }}>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                            <MyAppText style={styles.sideBarTitle}>{translate('drpScenarios')}</MyAppText>
                            {calculateSla(data)}
                        </View>
                        <View style={{ gap: 8 }}>
                            {Object.values(data.reports.reduce<Record<string, { name: string; color: string; count: number; }>>((reasons, { reason }) => {
                                if (!reasons[reason.name]) {
                                    reasons[reason.name] = { name: reason.name, color: reason.color, count: 0 };
                                }
                                reasons[reason.name].count++;
                                return reasons;
                            }, {})).map(r => <Badge
                                key={r.name}
                                mode='ROUNDED'
                                size={'small'}
                                backgroundColor={r.color}
                                textColor={getReasonTagTextColor(r.color)}
                                count={r.count}
                            >{r.name}</Badge>)}
                        </View>
                    </View>
                    <Divider color={theme.colors.backgrounds['BG-1']} />
                    <View style={{ gap: 8 }}>
                        <MyAppText style={styles.sideBarTitle}>{translate('cameraInfoLabel')}</MyAppText>
                        <View style={{ gap: 4 }}>
                            <SideBarItem title={translate('Id')} content={data.camera.id} direction='row' />
                            <SideBarItem title={translate('title')} content={data.camera.title} direction='column' />
                            <SideBarItem title={translate('serialNo')} content={removeDeletedDateFromSerialNo(data.camera.serialNo ?? '')} direction='row' />
                            <SideBarItem title={translate('address')} content={data.camera.address} direction='column' />
                            <SideBarItem title={translate('stolen_equipment')} content={data.camera.stolen ? translate('yes') : translate('no')} direction='row' />
                            <SideBarItem
                                title={translate('currentlyOnline')}
                                contentTextColor={data.camera.isConnected ? theme.colors.online.fontColor : theme.colors.offline.fontColor}
                                content={data.camera.isConnected ? translate('online') : translate('offline')} direction='row'
                            />
                            <SideBarItem title={translate('disabled')} content={data.camera.disabledAt !== null ? translate('yes') : translate('no')} direction='row' />
                            <SideBarItem title={translate('femaleDeleted')} content={data.camera.deletedAt !== null ? translate('yes') : translate('no')} direction='row' />
                        </View>
                    </View>
                    <Divider color={theme.colors.backgrounds['BG-1']} />
                    <View style={{ gap: 8 }}>
                        <MyAppText style={styles.sideBarTitle}>{translate('disconnectionData')}</MyAppText>
                        <View style={{ gap: 4 }}>
                            <SideBarItem title={translate('disconnectionDateTime')} content={moment(data.start).format('DD/MM/YYYY HH:mm:ss')} direction='column' />
                            <SideBarItem title={translate('duration')} content={getDuration(data.start, data.finish)} direction='column' />
                            {data.finish && <SideBarItem title={translate('reconnectionDateTime')} content={moment(data.finish).format('DD/MM/YYYY HH:mm:ss')} direction='column' />}
                        </View>
                    </View>
                </ScrollView>
            </Card>
        </View>
        <EmailPreviewModal
            isModalVisible={isEmailPreviewModalVisible}
            onClose={() => {
                setIsEmailPreviewModalVisible(false);
            }}
            observation={form.watch('observation')}
            reason={selectedReason}
            formSubmit={sendReport}
            ticketId={route.params.id}
        />
        <DeleteModal
            isModalVisible={isConfirmEmailWithoutAckModalVisible}
            onSubmit={() => {
                setIsEmailPreviewModalVisible(true);
            }}
            setModalVisible={setIsConfirmEmailWithoutAckModalVisible}
            customMessage={translate('youAreNotResponsibleForTheTicketMessage')}
            onSubmitType='primary'
            customConfirm={translate('confirm')}
        />
    </Container>;
}

const stylesheet = createStyleSheet(theme => ({
    container: {
        flex: 1,
        flexDirection: 'row',
        gap: 16,
    },
    reports: {
        gap: 32,
        flex: 1,
        borderColor: theme.colors.backgrounds['BG-1'],
        borderWidth: 1,
        borderRadius: 4,
    },
    cardTitle: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.neutral[500],
    },
    cardTitleContainer: {
        height: 24,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    reportHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: 8,
        alignItems: 'center',
    },
    reportDate: {
        fontSize: 12,
        fontWeight: '600',
        color: theme.colors.neutral[400],
    },
    reportUser: {
        fontSize: 14,
        fontWeight: '600',
        color: theme.colors.neutral[500],
    },
    reportDetail: {
        fontSize: 12,
        color: theme.colors.neutral[400],
    },
    sideBarTitle: {
        fontSize: 14,
        fontWeight: '600',
    },
    statusText: {
        fontSize: 12,
    },
    ackText: {
        fontSize: 14,
        color: theme.colors.primary[500],
        textDecorationLine: 'underline',
        textDecorationColor: theme.colors.primary[500],
    },
    sideBarItemTitle: {
        fontSize: 14,
        color: theme.colors.neutral[500],
        fontWeight: '600',
    },
    sideBarItemContent: {
        fontSize: 14,
        color: theme.colors.neutral[400],
    },
    userPin: {
        width: 40,
        height: 40,
        borderRadius: 50,
        aspectRatio: 1,
        borderWidth: 1,
        borderColor: theme.colors.dataTable.border,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#FFF'
    }
}));
